import { isErrorEnvelope, type ErrorEnvelope } from './errorEnvelope.js';

/**
 * Thin fetch wrapper for `t/:slug/api/v1/**`. Same-origin in production
 * (single sakhtar-cloud-prod-api deployable serves both API and this SPA's
 * built assets — docs/agents/customer-delivery-ui-route-inventory.md), so no
 * base URL configuration is needed; `vite.config.ts`'s dev proxy covers local
 * development only.
 *
 * Session auth is cookie-based (`credentials: 'include'`). No CSRF header is
 * attached here — verified against apps/api that `x-csrf-proof` is required
 * only for the auth session lifecycle endpoints (refresh/logout/mfa), which
 * go through `auth.ts` instead of this generic client.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly envelope: ErrorEnvelope;

  constructor(status: number, envelope: ErrorEnvelope) {
    super(envelope.error.message);
    this.status = status;
    this.envelope = envelope;
    this.name = 'ApiError';
  }

  get code(): ErrorEnvelope['error']['code'] {
    return this.envelope.error.code;
  }
}

export interface ApiRequestInit extends Omit<RequestInit, 'body'> {
  readonly body?: unknown;
}

async function request<T>(path: string, init: ApiRequestInit = {}): Promise<T> {
  const { body, headers, ...rest } = init;
  const requestInit: RequestInit = {
    ...rest,
    credentials: 'include',
    headers: {
      ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
      ...headers,
    },
  };
  if (body !== undefined) requestInit.body = JSON.stringify(body);

  const response = await fetch(path, requestInit);

  const text = await response.text();
  const parsed: unknown = text.length > 0 ? JSON.parse(text) : undefined;

  if (!response.ok) {
    if (typeof window !== 'undefined' && window.location && isErrorEnvelope(parsed)) {
      const match = window.location.pathname.match(/^\/t\/([^/]+)/);
      const tenantSlug = match?.[1];
      const onLogin = window.location.pathname.includes('/login');
      if (tenantSlug && !onLogin) {
        if (parsed.error.code === 'MEMBERSHIP_DISABLED') {
          try {
            sessionStorage.setItem('sakhtar.membershipDisabled', '1');
          } catch {
            // private-mode / blocked storage — LoginPage still handles the login 403
          }
          window.location.href = `/t/${tenantSlug}/login`;
        } else if (response.status === 401) {
          window.location.href = `/t/${tenantSlug}/login`;
        }
      }
    } else if (response.status === 401 && typeof window !== 'undefined' && window.location) {
      const match = window.location.pathname.match(/^\/t\/([^/]+)/);
      if (match && match[1] && !window.location.pathname.includes('/login')) {
        const tenantSlug = match[1];
        window.location.href = `/t/${tenantSlug}/login`;
      }
    }
    if (isErrorEnvelope(parsed)) {
      throw new ApiError(response.status, parsed);
    }
    throw new ApiError(response.status, {
      error: {
        code: 'INTERNAL',
        message: `Request failed with status ${response.status}`,
        requestId: '',
      },
    });
  }

  return parsed as T;
}

export function apiGet<T>(path: string, init?: ApiRequestInit): Promise<T> {
  return request<T>(path, { ...init, method: 'GET' });
}

/**
 * Most `t/:slug/api/v1/**` mutations require an `idempotency-key` header
 * (`apps/api/src/http/idempotency.ts`'s `IdempotencyGuard`, applied to
 * every write route not in `main.ts`'s narrow `idempotencyExempt` set).
 * Confirmed live against a running API: several call sites in this app
 * originally sent no key at all and got a 400 `VALIDATION_FAILED` back —
 * so every write helper below attaches one automatically unless the
 * caller already supplied one, rather than leaving each page to remember.
 */
function withIdempotencyKey(init: ApiRequestInit | undefined): ApiRequestInit {
  const headers = (init?.headers ?? {}) as Record<string, string>;
  if ('idempotency-key' in headers) return init ?? {};
  return { ...init, headers: { ...headers, 'idempotency-key': crypto.randomUUID() } };
}

export function apiPost<T>(path: string, body?: unknown, init?: ApiRequestInit): Promise<T> {
  return request<T>(path, { ...withIdempotencyKey(init), method: 'POST', body });
}

export function apiPatch<T>(path: string, body?: unknown, init?: ApiRequestInit): Promise<T> {
  return request<T>(path, { ...withIdempotencyKey(init), method: 'PATCH', body });
}

export function apiPut<T>(path: string, body?: unknown, init?: ApiRequestInit): Promise<T> {
  return request<T>(path, { ...withIdempotencyKey(init), method: 'PUT', body });
}

export function apiDelete<T>(path: string, init?: ApiRequestInit): Promise<T> {
  return request<T>(path, { ...withIdempotencyKey(init), method: 'DELETE' });
}

export { request as apiRequest };
